import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
// Import social icons
import { FaFacebook, FaInstagram, FaYoutube } from "react-icons/fa";
import { FaXTwitter } from "react-icons/fa6"; // X icon is often in fa6

const SidebarContainer = styled.nav`
  width: 200px;
  height: 100%;
  padding: 30px 20px;
  display: flex;
  flex-direction: column;
  background-color: #fff;
  box-sizing: border-box;
`;

// New Divider component
const Divider = styled.hr`
  border: none; /* Remove default hr border */
  border-top: 1px solid #eee; /* Style the top border */
  margin: 40px 0; /* Add vertical spacing */
`;

const NavLink = styled(Link)<{ isActive: boolean }>`
  color: ${props => props.isActive ? '#000' : '#555'};
  text-decoration: none;
  margin-bottom: 10px;
  font-size: 1.05rem;
  font-family: 'Inter', sans-serif;
  font-weight: ${props => props.isActive ? '600' : '400'};
  position: relative;
  transition: all 0.2s ease;
  padding-left: 15px;
  
  &:hover {
    color: #000;
  }
  
  &:before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background-color: ${props => props.isActive ? '#000' : 'transparent'};
    transition: all 0.2s ease;
  }
  
  &:hover:before {
    background-color: #000;
  }
`;

// New styled components for social links
const SocialLinksContainer = styled.div`
  margin-top: 40px; /* Add margin above the icons */
  padding-top: 20px; /* Space above links */
  display: flex;
  justify-content: space-around; /* Distribute icons evenly */
  align-items: center;
  border-top: 1px solid #eee; /* Optional separator line */
`;

const SocialLink = styled.a`
  color: #555; 
  font-size: 1.2rem; /* Adjust icon size */
  transition: color 0.2s ease;

  &:hover {
    color: #000;
  }
`;

const ChoralWorksBadgeWrapper = styled.div`
  margin-top: 14px;
  transform: scale(0.7);
  transform-origin: top left;
  width: 142.857%;
`;

const Sidebar: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    const existingScript = document.querySelector<HTMLScriptElement>('script[src="https://choral.works/embed/v1/badge.js"]');

    if (!existingScript) {
      const script = document.createElement('script');
      script.src = 'https://choral.works/embed/v1/badge.js';
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);
  
  return (
    <SidebarContainer>
      {/* Add divider above NavLinks */}
      <Divider />
      
      <NavLink to="/" isActive={location.pathname === '/'}>home</NavLink>
      <NavLink to="/bio" isActive={location.pathname === '/bio'}>bio</NavLink>
      <NavLink to="/works" isActive={location.pathname.startsWith('/works')}>works</NavLink>
      <NavLink to="/commissions" isActive={location.pathname === '/commissions'}>commissions</NavLink>
      <NavLink to="/contact" isActive={location.pathname === '/contact'}>contact</NavLink>

      {/* Social Links Section */}
      <SocialLinksContainer>
        <SocialLink href="https://www.facebook.com/rorywainwrightjohnston" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
          <FaFacebook />
        </SocialLink>
        <SocialLink href="https://www.instagram.com/rorywainwrightjohnston/" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
          <FaInstagram />
        </SocialLink>
        <SocialLink href="https://x.com/rorywainwright" target="_blank" rel="noopener noreferrer" aria-label="X (Twitter)">
          <FaXTwitter />
        </SocialLink>
        <SocialLink href="https://www.youtube.com/channel/UCTQvMtOgfuzfsfKMxqkYujw" target="_blank" rel="noopener noreferrer" aria-label="YouTube">
          <FaYoutube />
        </SocialLink>
      </SocialLinksContainer>
      <ChoralWorksBadgeWrapper>
        <div className="choralworks-embed" data-composer="rory-wainwright-johnston" data-theme="light"></div>
      </ChoralWorksBadgeWrapper>
    </SidebarContainer>
  );
};

export default Sidebar; 